import React, { useEffect, useState } from 'react'
import { useSky, setScene, stepPhase, SCENES, PHASE_LABELS } from '../hooks/useSky'
import { nextPhase } from '../lib/sky.mjs'

const NAMES = { bay: 'The Bay', desert: 'Sonoran desert', forest: 'Sierra forest' }

const ICONS = {
  bay: (
    <>
      <path d="M2 16h20" />
      <path d="M7 16V7M17 16V7" />
      <path d="M2 14.5C4.5 9 7 7 7 7s3.6 6.5 5 6.5S17 7 17 7s2.5 2 5 7.5" />
    </>
  ),
  desert: (
    <>
      <path d="M12 21V5" />
      <path d="M12 13H8.5V8" />
      <path d="M12 11h3.5V7" />
    </>
  ),
  forest: (
    <>
      <path d="M12 3 7 10h2.5L6 15.5h5V21" />
      <path d="M12 3l5 7h-2.5l3.5 5.5H13V21" />
    </>
  ),
}

// One glyph per time of day: the sun climbing over the horizon, the full
// sun, the sun low with long rays, the sun sinking, and the moon.
const PHASE_ICONS = {
  dawn: (
    <>
      <path d="M3 18h18M7.5 18a4.5 4.5 0 0 1 9 0" />
      <path d="M12 3v6M9.5 5.5 12 3l2.5 2.5M4.5 12.5 6 14M19.5 12.5 18 14" />
    </>
  ),
  day: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M5.3 5.3l1.5 1.5M17.2 17.2l1.5 1.5M5.3 18.7l1.5-1.5M17.2 6.8l1.5-1.5" />
    </>
  ),
  golden: (
    <>
      <path d="M2 17h20M8 17a4 4 0 0 1 8 0" />
      <path d="M12 7V5M6.5 9.5 5 8M17.5 9.5 19 8M2 12.5h2.5M19.5 12.5H22" />
    </>
  ),
  dusk: (
    <>
      <path d="M3 18h18M7.5 18a4.5 4.5 0 0 1 9 0" />
      <path d="M12 3v6M9.5 6.5 12 9l2.5-2.5M4.5 12.5 6 14M19.5 12.5 18 14" />
    </>
  ),
  night: (
    <>
      <path d="M20.5 14.2A8.5 8.5 0 0 1 9.8 3.5a7 7 0 1 0 10.7 10.7z" />
      <path d="M5 5v.01M4 10v.01" />
    </>
  ),
}

// The sun takes this long to reach its new height after the hour turns; the
// button's glyphs wait for it, so a click shows one change, not two.
const SUN_MOVE_MS = 950

// Scene picker in the masthead: one icon per landscape, the current one filled
// in, then the hour: a glyph for the time of day a click will bring, which
// turns the hour exactly as clicking the sun itself does. Once the sun has
// landed, the glyph moves on to the phase after that.
const SceneSwitch = () => {
  const sky = useSky()
  const current = (sky && sky.scene) || 'bay'
  const live = (sky && sky.phase) || 'golden'
  // The glyphs lag the store by the sun's move, so they change once it lands.
  const [phase, setPhase] = useState(live)
  useEffect(() => {
    if (live === phase) return undefined
    const id = setTimeout(() => setPhase(live), SUN_MOVE_MS)
    return () => clearTimeout(id)
  }, [live, phase])
  const next = nextPhase(phase)
  const hour = `Turn the hour: ${PHASE_LABELS[next]} next`
  return (
    <div className="scene-switch" role="group" aria-label="Scene">
      {SCENES.map((name) => (
        <button
          key={name}
          type="button"
          aria-pressed={current === name}
          aria-label={NAMES[name]}
          title={NAMES[name]}
          onClick={() => setScene(name)}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            {ICONS[name]}
          </svg>
        </button>
      ))}
      <span className="scene-switch-divider" aria-hidden="true" />
      <button type="button" className="sun-step" aria-label={hour} title={hour} onClick={() => stepPhase('masthead')}>
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          {PHASE_ICONS[next]}
        </svg>
      </button>
    </div>
  )
}

export default SceneSwitch
